import { useEffect, useRef } from 'react';

import { some } from 'lodash';
import { useRouter } from 'next/navigation';

import { IS_PRODUCTION } from '@/common/constants';

/**
 * Auto-resets error boundary on Fast Refresh in development.
 * Listens for HMR script injections to detect code changes.
 */
export function useDevErrorReset(reset: () => void) {
  const router = useRouter();
  const hasReset = useRef(false);

  useEffect(() => {
    if (IS_PRODUCTION) return;

    hasReset.current = false;

    const observer = new MutationObserver(mutations => {
      const hasHmrScripts = some(
        mutations,
        m =>
          m.type === 'childList' &&
          some(
            m.addedNodes,
            n => n.nodeName === 'SCRIPT' && (n as HTMLScriptElement).src?.includes('_next'),
          ),
      );

      if (hasHmrScripts && !hasReset.current) {
        hasReset.current = true;
        console.debug('[useDevErrorReset] HMR detected, resetting error boundary');
        router.refresh();
        reset();
      }
    });

    observer.observe(document.head, { childList: true });
    return () => observer.disconnect();
  }, [reset, router]);
}
